export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || (() => {
  // Environment-specific fallback based on hostname
  const hostname = window.location.hostname
  if (hostname.includes('dev.queuemein.com')) return 'https://api.dev.queuemein.com'
  if (hostname.includes('test.queuemein.com')) return 'https://api.test.queuemein.com'
  return 'https://api.' + 'queuemein.com' // production fallback
})()

interface ApiResponse<T> {
  ok?: boolean
  error?: string
  data?: T
}

interface Customer {
  phone: string
  name?: string
  total_visits: number
  completed_services: number
  no_shows: number
  avg_wait_time: number
  first_visit: string
  last_visit: string
  sms_enabled: boolean
}

class ApiClient {
  private token: string | null = null
  private requestCache: Map<string, { data: any; timestamp: number }> = new Map()
  private readonly CACHE_DURATION = 15000 // 15 seconds

  setToken(token: string) {
    this.token = token
  }

  private getCacheKey(url: string, options: RequestInit): string {
    return `${url}_${JSON.stringify(options.headers || {})}`
  }

  private getCachedResponse<T>(cacheKey: string): T | null {
    const cached = this.requestCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data
    }
    return null
  }

  private setCachedResponse(cacheKey: string, data: any): void {
    this.requestCache.set(cacheKey, { data, timestamp: Date.now() })
    
    // Clean old cache entries (keep only last 50)
    if (this.requestCache.size > 50) {
      const entries = Array.from(this.requestCache.entries())
      entries.sort((a, b) => b[1].timestamp - a[1].timestamp)
      this.requestCache.clear()
      entries.slice(0, 50).forEach(([key, value]) => {
        this.requestCache.set(key, value)
      })
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    
    // Only cache GET requests
    const isGetRequest = !options.method || options.method.toUpperCase() === 'GET'
    let cacheKey = ''
    
    if (isGetRequest) {
      cacheKey = this.getCacheKey(url, options)
      const cached = this.getCachedResponse<T>(cacheKey)
      if (cached) {
        console.log('API Cache Hit:', { url })
        return cached
      }
    }
    
    // Check for impersonation and add header
    const impersonationHeaders: Record<string, string> = {}
    const storedImpersonation = localStorage.getItem('impersonation')
    if (storedImpersonation) {
      try {
        const impersonation = JSON.parse(storedImpersonation)
        impersonationHeaders['X-Impersonate-Tenant'] = impersonation.impersonatedUser.tenant_id
        impersonationHeaders['X-Impersonate-User'] = impersonation.impersonatedUser.email
      } catch (e) {
        console.warn('Failed to parse impersonation data for headers:', e)
      }
    }
    
    const headers = {
      'Content-Type': 'application/json',
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...impersonationHeaders,
      ...options.headers,
    }

    console.log('API Request Debug:', {
      url,
      hasToken: !!this.token,
      tokenPreview: this.token ? `${this.token.substring(0, 20)}...` : 'none',
      headers: Object.keys(headers),
      impersonation: !!storedImpersonation
    })

    const response = await fetch(url, {
      ...options,
      headers,
    })

    console.log('API Response Debug:', {
      url,
      status: response.status,
      ok: response.ok,
      statusText: response.statusText
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('API Error Details:', {
        url,
        status: response.status,
        statusText: response.statusText,
        errorBody: errorText
      })
      throw new Error(`API Error: ${response.status} - ${errorText}`)
    }

    const result = await response.json()
    console.log('API Success Result:', { url, result })
    
    // Cache successful GET responses
    if (isGetRequest && cacheKey) {
      this.setCachedResponse(cacheKey, result)
    }
    
    return result
  }

  // Auth
  async getMe() {
    console.log('getMe called - token status:', {
      hasToken: !!this.token,
      tokenPreview: this.token ? `${this.token.substring(0, 20)}...` : 'none'
    })
    return this.request<{
      email: string
      sub: string
      groups: string[]
      tenant_from_group: string | null
      timezone: string
      is_super_admin: boolean
    }>('/admin/me')
  }

  async updateMyProfile(data: { first_name: string; last_name: string; email: string; timezone?: string }) {
    return this.request<{
      ok: boolean
      first_name: string
      last_name: string
      email: string
      timezone?: string
      email_changed: boolean
    }>('/admin/me/profile', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async resolveSubdomain(subdomain: string) {
    return this.request<{subdomain: string, tenant_id: string}>(`/admin/resolve-subdomain?subdomain=${encodeURIComponent(subdomain)}`)
  }

  async getTenantBranding(tenantId: string) {
    return this.request<{
      tenant_id: string
      branding: {
        primary_color?: string
        secondary_color?: string
        admin_background_color?: string
        web_join_text_color?: string
        [key: string]: any
      }
    }>(`/admin/tenant/${tenantId}/branding`)
  }

  // Tenants
  async getTenants() {
    return this.request<{ tenants: any[] }>('/admin/tenants')
  }

  async getTenantSettings(tenantId: string) {
    return this.request<{
      tenant_id: string
      disable_sms: boolean
      allow_multi_called_default: boolean
      ai_assistant_enabled: boolean
      ai_assistant_plan: string
      ai_features: {
        natural_language: boolean
        intelligent_responses: boolean
        proactive_suggestions: boolean
        wait_time_predictions: boolean
      }
      subdomain: string
      cognito_client_id?: string
      plan?: string
      sms_credits?: number
      settings: {
        branding?: {
          company_name?: string
          logo_url?: string
          primary_color?: string
          secondary_color?: string
          custom_domain?: string
          sms_sender_name?: string
          support_email?: string
          support_phone?: string
          web_join_title?: string
          web_join_subtitle?: string
          web_join_footer?: string
          web_join_background_color?: string
          web_join_text_color?: string
          web_join_button_color?: string
          web_join_success_message?: string
          web_join_error_message?: string
        }
        [key: string]: any
      }
    }>(`/admin/tenant/${tenantId}/settings`)
  }
  
  // Generic GET/PUT/POST/DELETE methods for flexibility
  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint)
  }
  
  async put<T = any>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }
  
  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    })
  }
  
  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE'
    })
  }

  async updateTenantSettings(tenantId: string, settings: {
    disable_sms?: boolean
    allow_multi_called_default?: boolean
    ai_assistant_enabled?: boolean
    ai_assistant_plan?: string
    ai_features?: {
      natural_language?: boolean
      intelligent_responses?: boolean
      proactive_suggestions?: boolean
      wait_time_predictions?: boolean
    }
    settings?: {
      branding?: {
        company_name?: string
        logo_url?: string
        primary_color?: string
        secondary_color?: string
        custom_domain?: string
        subdomain?: string
        sms_sender_name?: string
        support_email?: string
        support_phone?: string
        web_join_title?: string
        web_join_subtitle?: string
        web_join_footer?: string
        web_join_background_color?: string
        web_join_text_color?: string
        web_join_button_color?: string
        web_join_success_message?: string
        web_join_error_message?: string
      }
      [key: string]: any
    }
  }) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  }

  async createTenant(data: { display_name: string; subdomain: string }) {
    return this.request<ApiResponse<any>>('/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async deleteTenant(tenantId: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}`, {
      method: 'DELETE',
    })
  }

  async updateTenantStatus(tenantId: string, status: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    })
  }

  // Queues
  async getQueuesSummary(tenantId: string) {
    const endpoint = `/admin/tenant/${tenantId}/queues/summary`
    console.log(`Making getQueuesSummary request:`, {
      endpoint,
      tenantId,
      hasToken: !!this.token,
      tokenPreview: this.token ? `${this.token.substring(0, 20)}...` : 'none'
    });
    
    try {
      const result = await this.request<{
        tenant: string
        allow_multi_called_default: boolean
        queues: Array<{
          location_id: string
          service_id: string
          waiting: number
          called: number
          being_serviced: number
          no_show: number
          archived: boolean
        }>
      }>(endpoint)
      
      console.log('getQueuesSummary success:', result);
      return result;
    } catch (error) {
      console.error('getQueuesSummary failed:', error);
      throw error;
    }
  }

  async getQueuePeek(tenantId: string, locationId: string, serviceId: string) {
    return this.request<{
      tenant_id: string
      location_id: string
      service_id: string
      counts: {
        waiting: number
        called: number
        being_serviced: number
        no_show: number
      }
      waiting: any[]
      called: any[]
      being_serviced: any[]
      no_show: any[]
    }>(`/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/peek`)
  }

  // Queue Actions
  async callNext(tenantId: string, locationId: string, serviceId: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/call_next`, {
      method: 'POST',
    })
  }

  async closeCurrent(tenantId: string, locationId: string, serviceId: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/close_current`, {
      method: 'POST',
    })
  }

  async markArrived(tenantId: string, locationId: string, serviceId: string, entryId: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/mark_arrived`, {
      method: 'POST',
      body: JSON.stringify({ entry_id: entryId }),
    })
  }

  async markNoShow(tenantId: string, locationId: string, serviceId: string, entryId: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/mark_no_show`, {
      method: 'POST',
      body: JSON.stringify({ entry_id: entryId }),
    })
  }

  // Users
  async getUsers(tenantId: string) {
    return this.request<{ users: any[] }>(`/admin/tenant/${tenantId}/users`)
  }

  async createUser(tenantId: string, data: { email: string; role: string; first_name: string; last_name: string; send_email?: boolean }) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async disableUser(tenantId: string, username: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/users/${username}/disable`, {
      method: 'PUT',
    })
  }

  async deleteUser(tenantId: string, username: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/users/${username}`, {
      method: 'DELETE',
    })
  }

  async enableUser(tenantId: string, username: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/users/${username}/enable`, {
      method: 'PUT',
    })
  }

  async resetUserPassword(tenantId: string, username: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/users/${username}/reset_password`, {
      method: 'PUT',
    })
  }

  async changeUserRole(tenantId: string, username: string, role: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/users/${username}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    })
  }

  async updateUser(tenantId: string, username: string, data: { first_name: string; last_name: string; email: string; timezone?: string }) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/users/${username}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Customers
  async getCustomers(tenantId: string) {
    return this.request<{ customers: Array<{
      phone: string
      name?: string
      total_visits: number
      completed_services: number
      no_shows: number
      avg_wait_time: number
      first_visit: string
      last_visit: string
      sms_enabled: boolean
    }> }>(`/admin/tenant/${tenantId}/customers`)
  }

  async getCustomerDetail(tenantId: string, phone: string) {
    return this.request<{
      profile: {
        phone: string
        name?: string
        total_visits: number
        completed_services: number
        no_shows: number
        avg_wait_time: number
        first_visit: string
        last_visit: string
      }
      sms_preferences: {
        sms_enabled: boolean
        opt_out_date?: string
      }
      queue_history: Array<{
        entry_id: string
        location_id: string
        service_id: string
        joined_at: number
        status: string
        wait_time?: number
        service_time?: number
      }>
    }>(`/admin/tenant/${tenantId}/customer/${encodeURIComponent(phone)}`)
  }

  async updateCustomerDetail(tenantId: string, phone: string, data: {
    name?: string
    sms_enabled?: boolean
  }) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/customer/${encodeURIComponent(phone)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteCustomer(tenantId: string, phone: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/customer/${encodeURIComponent(phone)}`, {
      method: 'DELETE',
    })
  }

  // Queue Configuration (Admin - requires auth)
  async getAdminQueueConfig(tenantId: string, locationId: string, serviceId: string) {
    return this.request<any>(`/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/config`)
  }

  async saveQueueConfig(tenantId: string, data: any) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/queue`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteQueue(tenantId: string, locationId: string, serviceId: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}`, {
      method: 'DELETE',
    })
  }

  async archiveQueue(tenantId: string, locationId: string, serviceId: string, archived: boolean) {
    const endpoint = archived 
      ? `/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/archive`
      : `/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/unarchive`
    
    return this.request<ApiResponse<any>>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ archived }),
    })
  }

  // Public queue config (no auth required)
  async getQueueConfig(tenantId: string, locationId: string, serviceId: string) {
    return fetch(`${API_BASE_URL}/join/config?tenant=${tenantId}&location_id=${locationId}&service_id=${serviceId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then(res => {
      if (!res.ok) {
        throw new Error(`Queue not found: ${res.status}`)
      }
      return res.json()
    })
  }

  // Public join queue (no auth required)
  async joinQueue(data: {
    tenant_id: string
    location_id: string
    service_id: string
    phone: string
    name?: string
    party_size?: number
    agree_version: string
    meta: { answers: Record<string, string> }
  }) {
    return fetch(`${API_BASE_URL}/join/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(res => res.json())
  }

  // Clear cache for specific endpoints
  clearCache(pattern?: string) {
    if (pattern) {
      // Clear cache entries matching pattern
      const keysToDelete = Array.from(this.requestCache.keys()).filter(key => key.includes(pattern))
      keysToDelete.forEach(key => this.requestCache.delete(key))
      console.log(`Cleared ${keysToDelete.length} cache entries matching pattern: ${pattern}`)
    } else {
      // Clear all cache
      this.requestCache.clear()
      console.log('Cleared all API cache')
    }
  }

  // Queue Management
  async getQueueEntries(tenantId: string, locationId: string, serviceId: string) {
    return this.request<{
      counts: { waiting: number; called: number; being_serviced: number; no_show: number; delayed?: number }
      waiting: any[]
      called: any[]
      being_serviced: any[]
      no_show: any[]
      delayed?: any[]
    }>(`/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/peek`)
  }

  async updateQueueEntry(tenantId: string, locationId: string, serviceId: string, entryId: string, action: string) {
    const endpoint = `/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/${action}`
    
    // All actions need entry_id in body for proper processing
    return this.request<ApiResponse<any>>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ entry_id: entryId }),
    })
  }

  async removeQueueEntry(tenantId: string, locationId: string, serviceId: string, entryId: string, reason: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/remove_entry`, {
      method: 'POST',
      body: JSON.stringify({ entry_id: entryId, reason }),
    })
  }

  // SMS History
  async getSmsHistory(tenantId: string, locationId: string, serviceId: string, params?: { phone?: string; entryId?: string; date_from?: string; date_to?: string; timezone?: string }) {
    const queryParams = new URLSearchParams()
    if (params?.phone) queryParams.append('phone', params.phone)
    if (params?.entryId) queryParams.append('sk', params.entryId)
    if (params?.date_from) queryParams.append('date_from', params.date_from)
    if (params?.date_to) queryParams.append('date_to', params.date_to)
    if (params?.timezone) queryParams.append('timezone', params.timezone)
    
    const url = `/admin/tenant/${tenantId}/queue/${locationId}/${serviceId}/sms_history?${queryParams.toString()}`
    console.log('SMS History API call:', { url, params, tenantId, locationId, serviceId })
    
    return this.request<{
      messages: Array<{
        ts: number
        direction: string
        body: string
        event: string
        by: string
        entry_id: string
      }>
      tenant_id: string
      location_id: string
      service_id: string
    }>(url)
  }

  // SMS Templates
  async getSmsTemplates(tenantId: string) {
    return this.request<{
      templates: Record<string, {
        template_type: string
        message: string
        enabled: boolean
      }>
    }>(`/admin/tenant/${tenantId}/sms/templates`)
  }

  async saveSmsTemplate(tenantId: string, templateType: string, template: {
    template_type: string
    message: string
    enabled: boolean
  }) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/sms/templates/${templateType}`, {
      method: 'PUT',
      body: JSON.stringify(template),
    })
  }

  // Image Upload
  async uploadImage(formData: FormData, params: { tenant: string; location: string; service: string }) {
    const url = `${API_BASE_URL}/admin/upload-image?tenant=${params.tenant}&location=${encodeURIComponent(params.location)}&service=${encodeURIComponent(params.service)}`
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`)
    }

    return response.json()
  }

  // Lex Configuration
  async getLexConfig(tenantId?: string) {
    const endpoint = tenantId 
      ? `/admin/tenant/${tenantId}/lex/config`
      : '/admin/lex/config'
      
    return this.request<{
      success: boolean
      botConfig?: {
        botId: string
        botAliasId: string
        botName: string
        description: string
        status: string
        region: string
        tenantId?: string
        isShared: boolean
        locales: Array<{
          localeId: string
          localeName: string
          description: string
          intents: Array<{
            intentId: string
            intentName: string
            description: string
            sampleUtterances: string[]
            slots: Array<{
              slotName: string
              slotTypeId: string
              valueElicitationPrompt: any[]
              required: boolean
            }>
            fulfillmentCodeHook: any
            confirmationSetting: any
          }>
          slotTypes: Array<{
            slotTypeId: string
            slotTypeName: string
            description: string
            slotTypeValues: Array<{
              sampleValue: string
              synonyms: string[]
            }>
            valueSelectionStrategy: string
          }>
        }>
      }
      error?: string
    }>(endpoint)
  }

  // Lex Intent Management (Super Admin only)
  async getLexIntents() {
    return this.request<{
      intents: Array<{
        intentId: string
        intentName: string
        description: string
        lastUpdatedDateTime?: string
      }>
    }>('/admin/lex/intents')
  }

  async getLexIntent(intentId: string) {
    return this.request<{
      intent: {
        intentId: string
        intentName: string
        description: string
        sampleUtterances: string[]
        slots: any[]
        fulfillmentCodeHook: any
        confirmationSetting: any
      }
    }>(`/admin/lex/intent/${intentId}`)
  }

  async createLexIntent(data: {
    intentName: string
    description?: string
    sampleUtterances: string[]
  }) {
    return this.request<{
      success: boolean
      intentId: string
      intentName: string
      error?: string
    }>('/admin/lex/intents', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateLexIntent(intentId: string, data: {
    intentName?: string
    description?: string
    sampleUtterances: string[]
  }) {
    return this.request<{
      success: boolean
      intentId: string
      intentName: string
      error?: string
    }>(`/admin/lex/intent/${intentId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async deleteLexIntent(intentId: string) {
    return this.request<{
      success: boolean
      message: string
      error?: string
    }>(`/admin/lex/intent/${intentId}`, {
      method: 'DELETE'
    })
  }

  // Reports
  async getAvgWaitTimesReport(params: { tenant?: string; date_from?: string; date_to?: string; timezone?: string }) {
    const queryParams = new URLSearchParams()
    if (params.tenant) queryParams.append('tenant', params.tenant)
    if (params.date_from) queryParams.append('date_from', params.date_from)
    if (params.date_to) queryParams.append('date_to', params.date_to)
    if (params.timezone) queryParams.append('timezone', params.timezone)
    
    return this.request<{
      report_type: string
      date_range: { from: string; to: string }
      summary: {
        overall_avg_wait: string
        peak_avg_wait: string
        off_peak_avg_wait: string
        total_samples: number
      }
      data: Array<{
        tenant: string
        location: string
        service: string
        avg_wait_minutes: number
        sample_size: number
        min_wait: number
        max_wait: number
      }>
    }>(`/admin/reports/avg-wait-times?${queryParams.toString()}`)
  }

  async getQueueDetailsReport(params: { tenant?: string; date_from?: string; date_to?: string; timezone?: string }) {
    const queryParams = new URLSearchParams()
    if (params.tenant) queryParams.append('tenant', params.tenant)
    if (params.date_from) queryParams.append('date_from', params.date_from)
    if (params.date_to) queryParams.append('date_to', params.date_to)
    if (params.timezone) queryParams.append('timezone', params.timezone)
    
    return this.request<any>(`/admin/reports/queue-details?${queryParams.toString()}`)
  }

  async getQueueThroughputReport(params: { tenant?: string; date_from?: string; date_to?: string; timezone?: string }) {
    const queryParams = new URLSearchParams()
    if (params.tenant) queryParams.append('tenant', params.tenant)
    if (params.date_from) queryParams.append('date_from', params.date_from)
    if (params.date_to) queryParams.append('date_to', params.date_to)
    if (params.timezone) queryParams.append('timezone', params.timezone)
    
    return this.request<any>(`/admin/reports/queue-throughput?${queryParams.toString()}`)
  }

  async getHourlyActivityReport(params: { tenant?: string; date_from?: string; date_to?: string; timezone?: string }) {
    const queryParams = new URLSearchParams()
    if (params.tenant) queryParams.append('tenant', params.tenant)
    if (params.date_from) queryParams.append('date_from', params.date_from)
    if (params.date_to) queryParams.append('date_to', params.date_to)
    if (params.timezone) queryParams.append('timezone', params.timezone)
    
    return this.request<{
      report_type: string
      date_range: { from: string; to: string }
      timezone: string
      summary: {
        total_entries: number
        total_service_starts: number
        total_completions: number
        peak_entry_hour: string
        peak_service_hour: string
        peak_completion_hour: string
      }
      data: Array<{
        hour: string
        entries: number
        service_starts: number
        completions: number
      }>
    }>(`/admin/reports/hourly-activity?${queryParams.toString()}`)
  }

  async getCustomerSearchReport(params: { search: string; tenant?: string; date_from?: string; date_to?: string; timezone?: string }) {
    const queryParams = new URLSearchParams()
    queryParams.append('search', params.search)
    if (params.tenant) queryParams.append('tenant', params.tenant)
    if (params.date_from) queryParams.append('date_from', params.date_from)
    if (params.date_to) queryParams.append('date_to', params.date_to)
    if (params.timezone) queryParams.append('timezone', params.timezone)
    
    return this.request<any>(`/admin/reports/customer-search?${queryParams.toString()}`)
  }

  // SMS Analytics
  async getSmsAnalytics(tenantId: string, params: { date_from?: string; date_to?: string; timezone?: string }) {
    const queryParams = new URLSearchParams()
    if (params.date_from) queryParams.append('date_from', params.date_from)
    if (params.date_to) queryParams.append('date_to', params.date_to)
    if (params.timezone) queryParams.append('timezone', params.timezone)
    
    return this.request<{
      total_outbound: number
      total_inbound: number
      total_cost: number
      daily_breakdown: Array<{
        date: string
        outbound: number
        inbound: number
        cost: number
      }>
      location_breakdown: Array<{
        location_id: string
        service_id: string
        outbound: number
        inbound: number
        cost: number
      }>
      trigger_breakdown: Array<{
        triggered_by: string
        count: number
        cost: number
      }>
    }>(`/admin/tenant/${tenantId}/sms/analytics?${queryParams.toString()}`)
  }

  // SMS Details
  async getSmsDetails(tenantId: string, params: { date_from?: string; date_to?: string; timezone?: string; location_id?: string; service_id?: string }) {
    const queryParams = new URLSearchParams()
    if (params.date_from) queryParams.append('date_from', params.date_from)
    if (params.date_to) queryParams.append('date_to', params.date_to)
    if (params.timezone) queryParams.append('timezone', params.timezone)
    if (params.location_id) queryParams.append('location_id', params.location_id)
    if (params.service_id) queryParams.append('service_id', params.service_id)
    
    return this.request<{
      tenant_id: string
      date_range: { from: string; to: string }
      filter: { location_id?: string; service_id?: string }
      messages: Array<{
        timestamp: number
        datetime: string
        direction: string
        phone: string
        message: string
        cost: number
        location_id: string
        service_id: string
        triggered_by: string
      }>
      total_messages: number
    }>(`/admin/tenant/${tenantId}/sms/details?${queryParams.toString()}`)
  }

  // Plan Limits
  async getPlanLimits(tenantId: string) {
    return this.request<{
      tenant_id: string
      plan: string
      limit: number
      used: number
      outbound: number
      inbound: number
      usage_percentage: number
      is_over_limit: boolean
      is_near_limit: boolean
      remaining: number
    }>(`/admin/tenant/${tenantId}/plan/limits`)
  }

  async checkSmsLimit(tenantId: string) {
    return this.request<{
      can_send: boolean
      limits: {
        tenant_id: string
        plan: string
        limit: number
        used: number
        usage_percentage: number
        is_over_limit: boolean
        is_near_limit: boolean
        remaining: number
      }
    }>(`/admin/tenant/${tenantId}/plan/check-sms`, {
      method: 'POST'
    })
  }

  // SMS Bundle Management
  async getSmsBundles() {
    return this.request<{
      bundles: Array<{
        size: number
        price: number
        price_per_message: number
      }>
    }>('/admin/sms/bundles')
  }

  async purchaseSmsBundle(tenantId: string, bundleSize: number, purchasedBy: string, paymentReference?: string) {
    return this.request<{
      success: boolean
      purchase_id?: string
      bundle_size?: number
      price?: number
      message?: string
      error?: string
    }>(`/admin/tenant/${tenantId}/sms/purchase`, {
      method: 'POST',
      body: JSON.stringify({
        bundle_size: bundleSize,
        purchased_by: purchasedBy,
        payment_reference: paymentReference
      })
    })
  }

  async getSmsPurchases(tenantId: string) {
    return this.request<{
      purchases: Array<{
        purchase_id: string
        purchase_type: string
        bundle_size: number
        price: number
        purchased_by: string
        purchased_at: number
        payment_reference: string
        status: string
      }>
    }>(`/admin/tenant/${tenantId}/sms/purchases`)
  }

  // Live Dashboard
  async getDashboard(tenantId: string) {
    return this.request<{
      tenant: string
      timestamp: number
      queues: Array<{
        location_id: string
        service_id: string
        waiting: Array<{
          name: string
          phone: string
          epoch: number
          strikes: number
          status: string
          wait_minutes: number
        }>
        called: Array<{
          name: string
          phone: string
          epoch: number
          strikes: number
          status: string
          wait_minutes: number
        }>
        being_serviced: Array<{
          name: string
          phone: string
          epoch: number
          strikes: number
          status: string
          wait_minutes: number
        }>
        no_show: Array<{
          name: string
          phone: string
          epoch: number
          strikes: number
          status: string
          wait_minutes: number
        }>
      }>
    }>(`/admin/dashboard/${tenantId}`)
  }

  // Analytics Dashboard
  async getAnalyticsDashboard(tenantId: string, params?: { date_from?: string; date_to?: string; timezone?: string }) {
    const queryParams = new URLSearchParams()
    if (params?.date_from) queryParams.append('date_from', params.date_from)
    if (params?.date_to) queryParams.append('date_to', params.date_to)
    if (params?.timezone) queryParams.append('timezone', params.timezone)
    
    const queryString = queryParams.toString()
    const endpoint = `/admin/tenant/${tenantId}/analytics/dashboard${queryString ? `?${queryString}` : ''}`
    
    return this.request<{
      data: {
        summary: {
          total_customers: number
          completed_customers: number
          avg_wait_time_minutes: number
          abandonment_rate_percent: number
          no_show_rate_percent: number
          completion_rate_percent: number
          busiest_hour: string
        }
        roi_metrics: {
          estimated_revenue: number
          roi_percent: number
        }
        trends: {
          customer_satisfaction_score: number
        }
      }
    }>(endpoint)
  }

  // System Status (Super Admin only)
  async getSystemStatus() {
    return this.request<{
      uptime_percentage: number
      avg_response_time_ms: number
      sms_delivery_rate: number
      active_incidents: number
      services: Array<{
        name: string
        description: string
        status: 'operational' | 'degraded' | 'outage'
      }>
      recent_updates: Array<{
        id: string
        date: string
        title: string
        description: string
      }>
    }>('/admin/system-status')
  }

  // Public system status (no auth required)
  async getPublicSystemStatus() {
    return fetch(`${API_BASE_URL}/system-status`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }).then(res => {
      if (!res.ok) {
        throw new Error(`Failed to fetch status: ${res.status}`)
      }
      return res.json()
    })
  }

  async updateSystemStatus(data: {
    uptime_percentage: number
    avg_response_time_ms: number
    sms_delivery_rate: number
    active_incidents: number
    services: Array<{
      name: string
      description: string
      status: 'operational' | 'degraded' | 'outage'
    }>
    recent_updates: Array<{
      id: string
      date: string
      title: string
      description: string
    }>
  }) {
    return this.request<ApiResponse<any>>('/admin/system-status', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  // Subscription Management
  async getSubscription(tenantId: string) {
    return this.request<{
      tenant_id: string
      current_plan: string
      plan_info: {
        name: string
        price: number
        sms_limit: number
        features: string[]
      }
      addons: Record<string, boolean>
      billing_cycle: string
      subscription_status: string
      trial_end_date?: string
      next_billing_date?: string
      is_trial: boolean
    }>(`/admin/tenant/${tenantId}/subscription`)
  }

  async getPlans() {
    return this.request<{
      plans: Array<{
        plan_id: string
        name: string
        price: number
        sms_limit: number
        features: string[]
      }>
    }>('/admin/plans')
  }

  async getAddons() {
    return this.request<{
      addons: Array<{
        addon_id: string
        name: string
        price: number
        description: string
      }>
    }>('/admin/addons')
  }

  async changePlan(tenantId: string, plan: string, changedBy: string) {
    return this.request<{
      success: boolean
      new_plan: string
      plan_info: any
      change_id: string
    }>(`/admin/tenant/${tenantId}/subscription/plan`, {
      method: 'PUT',
      body: JSON.stringify({ plan, changed_by: changedBy })
    })
  }

  async toggleAddon(tenantId: string, addonId: string, enabled: boolean, changedBy: string) {
    return this.request<{
      success: boolean
      addon_id: string
      enabled: boolean
      addon_info: any
      change_id: string
    }>(`/admin/tenant/${tenantId}/subscription/addon`, {
      method: 'PUT',
      body: JSON.stringify({ addon_id: addonId, enabled, changed_by: changedBy })
    })
  }

  async cancelSubscription(tenantId: string, cancelledBy: string) {
    return this.request<{
      success: boolean
      status: string
      cancelled_at: number
    }>(`/admin/tenant/${tenantId}/subscription`, {
      method: 'DELETE',
      body: JSON.stringify({ cancelled_by: cancelledBy })
    })
  }

  async getBillingHistory(tenantId: string, limit?: number) {
    const queryParams = new URLSearchParams()
    if (limit) queryParams.append('limit', limit.toString())
    
    return this.request<{
      history: Array<{
        record_id: string
        change_type: string
        old_plan?: string
        new_plan?: string
        addon_id?: string
        enabled?: boolean
        bundle_size?: number
        price?: number
        changed_by: string
        changed_at: number
      }>
    }>(`/admin/tenant/${tenantId}/billing/history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`)
  }

  async getServiceAccess(tenantId: string) {
    return this.request<{
      has_access: boolean
      reason: string
      expires_at?: number
    }>(`/admin/tenant/${tenantId}/service/access`)
  }

  async getPhoneStatus(tenantId: string) {
    return this.request<{
      has_dedicated_phone: boolean
      shared_phone: string
      dedicated_phone?: string
    }>(`/admin/tenant/${tenantId}/phone/status`)
  }

  // Locations
  async getLocations(tenantId: string) {
    return this.request<{
      locations: Array<{
        location_id: string
        name: string
        address?: string
        phone?: string
        timezone: string
        active: boolean
        created_at: number
        updated_at: number
      }>
      count: number
      max_allowed: number
    }>(`/admin/tenant/${tenantId}/locations`)
  }

  async createLocation(tenantId: string, data: {
    location_id: string
    name: string
    address?: string
    phone?: string
  }) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/locations`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateLocation(tenantId: string, locationId: string, data: {
    name: string
    address?: string
    phone?: string
  }) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/locations/${locationId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async deleteLocation(tenantId: string, locationId: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/locations/${locationId}`, {
      method: 'DELETE'
    })
  }

  // Services
  async getServices(tenantId: string) {
    return this.request<{
      services: Array<{
        service_id: string
        name: string
        description?: string
        estimated_minutes: number
        active: boolean
        created_at: number
        updated_at: number
      }>
      count: number
      max_allowed: number
    }>(`/admin/tenant/${tenantId}/services`)
  }

  async createService(tenantId: string, data: {
    service_id: string
    name: string
    description?: string
    estimated_minutes: number
  }) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/services`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateService(tenantId: string, serviceId: string, data: {
    name: string
    description?: string
    estimated_minutes: number
  }) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/services/${serviceId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async deleteService(tenantId: string, serviceId: string) {
    return this.request<ApiResponse<any>>(`/admin/tenant/${tenantId}/services/${serviceId}`, {
      method: 'DELETE'
    })
  }

  // SMS Credits
  async getSmsCredits(tenantId: string) {
    return this.request<any>(`/admin/tenant/${tenantId}/sms/credits-summary`)
  }

  async grantSmsCredits(tenantId: string, credits: number) {
    return this.request<{
      success: boolean
      credits_granted: number
      new_balance: number
    }>(`/admin/tenant/${tenantId}/sms-credits/grant`, {
      method: 'POST',
      body: JSON.stringify({ credits })
    })
  }

  async purchaseSmsCredits(tenantId: string, credits: number, price: number, paymentData: any) {
    return this.request<{
      success: boolean
      purchase_id: string
      credits: number
    }>(`/admin/tenant/${tenantId}/sms/credits/purchase`, {
      method: 'POST',
      body: JSON.stringify({
        credits,
        price,
        payment_data: paymentData
      })
    })
  }

  // Stripe Payments
  async createPaymentIntent(data: {
    amount: number
    currency: string
    tenant_id: string
    plan: string
  }) {
    return this.request<{
      client_secret: string
      payment_intent_id: string
    }>('/stripe/create-payment-intent', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

}

export const apiClient = new ApiClient()
export type { ApiResponse, Customer }

export interface CustomerDetail {
  profile: {
    phone: string
    name?: string
    total_visits: number
    completed_services: number
    no_shows: number
    avg_wait_time: number
    first_visit: string
    last_visit: string
  }
  sms_preferences: {
    sms_enabled: boolean
    opt_out_date?: string
  }
  queue_history: Array<{
    entry_id: string
    location_id: string
    service_id: string
    joined_at: number
    status: string
    wait_time?: number
    service_time?: number
  }>
}